// Imports
import ___CSS_LOADER_API_SOURCEMAP_IMPORT___ from "../../../../node_modules/css-loader/dist/runtime/sourceMaps.js";
import ___CSS_LOADER_API_IMPORT___ from "../../../../node_modules/css-loader/dist/runtime/api.js";
var ___CSS_LOADER_EXPORT___ = ___CSS_LOADER_API_IMPORT___(___CSS_LOADER_API_SOURCEMAP_IMPORT___);
// Module
___CSS_LOADER_EXPORT___.push([module.id, `/*
 * Connected services (Settings).
 *
 * Every colour is a design token (src/styles/theme.css) so both themes stay
 * correct — no hardcoded hex anywhere, per DESIGN_GUIDE.md. Buttons, badges,
 * cards, form inputs and the modal shell come from the atoms and the global
 * theme-overrides layer; this file only adds the layout that is specific to
 * this screen.
 *
 * The one inline style in the JSX is \`--consvc-accent\`, which is set to
 * \`var(--chart-N)\` from the provider descriptor's accentIndex. That is a custom
 * property pointing AT a token, not a colour literal: the accent has to be
 * chosen per provider at runtime, and a provider added on the server must get
 * its monogram colour with no CSS change here.
 */

/* Used as a hook in the JSX and previously styled nowhere. The bottom padding
   is load-bearing: the last card is \`mt-2\` with no bottom margin, so without it
   the page ended flush against the viewport edge on every layout that does not
   happen to render the optional "Not available" section. */
.consvc-page {
  padding-bottom: 1.5rem;
}

.consvc-page__head {
  padding: 0.25rem 0 0.5rem;
}

.consvc-page__title {
  color: var(--foreground);
  font-family: var(--font-sans);
  font-weight: 700;
}

.consvc-page__subtitle {
  color: var(--muted-foreground);
  font-size: 0.8125rem;
  max-width: 72ch;
}

/* ---------- "how a service connects" explainer ---------- */

/* Three short statements rather than a paragraph: the first one is the whole
   mental model (signing in IS the connect), and a reader who skims a paragraph
   misses it and then looks for a button that they mostly do not need. */
.consvc-how {
  display: grid;
  gap: 0.875rem;
  grid-template-columns: repeat(auto-fit, minmax(min(17rem, 100%), 1fr));
}

.consvc-how__item {
  border-left: 2px solid var(--border-strong);
  padding-left: 0.75rem;
}

.consvc-how__item--key {
  border-left-color: var(--primary);
}

.consvc-how__title {
  align-items: center;
  color: var(--foreground);
  display: flex;
  font-size: 0.8125rem;
  font-weight: 600;
  gap: 0.375rem;
  margin-bottom: 0.25rem;
}

.consvc-how__title i {
  color: var(--muted-foreground);
  font-size: 0.75rem;
}

.consvc-how__item--key .consvc-how__title i {
  color: var(--primary);
}

.consvc-how__text {
  color: var(--muted-foreground);
  font-size: 0.75rem;
  line-height: 1.5;
  margin: 0;
}

/* ---------- sections ---------- */

.consvc-section__hint {
  color: var(--muted-foreground);
  font-size: 0.75rem;
  line-height: 1.5;
  margin: 0 0 0.75rem;
  max-width: 78ch;
}

.consvc-section__count {
  color: var(--muted-foreground);
  font-family: var(--font-mono);
  font-size: 0.75rem;
  font-weight: 500;
}

/* ---------- one service row ---------- */

.consvc-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.consvc-row {
  align-items: flex-start;
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  display: flex;
  gap: 0.75rem;
  padding: 0.75rem;
  transition: background var(--transition-fast), border-color var(--transition-fast);
}

.consvc-row:hover {
  background: var(--card-hover);
}

/* Two states of a link, painted in order of severity. Stale is a warning: still
   on file and still usable, just not confirmed recently. Unusable is past the
   ceiling — it may NOT be used as an export target until somebody re-checks it,
   so it is the louder of the two, not the quieter. (It was originally painted
   inert, sharing a rule with "not available on this deployment", while carrying
   a danger badge — the ground and the chip disagreed about the same row.) */
.consvc-row--stale {
  background: var(--warning-soft);
  border-color: var(--warning);
}

.consvc-row--stale:hover {
  background: var(--warning-soft);
}

.consvc-row--unusable {
  background: var(--destructive-soft);
  border-color: var(--destructive);
}

.consvc-row--unusable:hover {
  background: var(--destructive-soft);
}

/* Not a state of a link — a statement about the deployment. It is inert, so it
   is the one row that reads as absent rather than as wrong. */
.consvc-row--unavailable {
  background: var(--muted);
  border-style: dashed;
}

.consvc-row--unavailable:hover {
  background: var(--muted);
}

.consvc-row__mono {
  align-items: center;
  background: var(--consvc-accent, var(--primary));
  border-radius: var(--radius-sm);
  color: var(--primary-foreground);
  display: flex;
  flex: 0 0 auto;
  font-family: var(--font-sans);
  font-size: 0.8125rem;
  font-weight: 700;
  height: 2.25rem;
  justify-content: center;
  letter-spacing: 0.01em;
  width: 2.25rem;
}

.consvc-row--unavailable .consvc-row__mono {
  background: var(--secondary);
  color: var(--secondary-foreground);
}

.consvc-row__body {
  flex: 1 1 auto;
  min-width: 0;
}

.consvc-row__name {
  align-items: center;
  color: var(--foreground);
  display: flex;
  flex-wrap: wrap;
  font-size: 0.875rem;
  font-weight: 600;
  gap: 0.375rem 0.5rem;
}

.consvc-row__site {
  color: var(--muted-foreground);
  font-weight: 500;
}

.consvc-row__meta {
  color: var(--muted-foreground);
  font-size: 0.75rem;
  line-height: 1.5;
  margin-top: 0.1875rem;
  overflow-wrap: anywhere;
}

/* The external id is the one thing an operator is ever asked to read back to
   Gemmiz support, so it is selectable as a whole rather than a word at a time. */
.consvc-row__id {
  background: var(--secondary);
  border-radius: var(--radius-sm);
  color: var(--secondary-foreground);
  font-family: var(--font-mono);
  font-size: 0.6875rem;
  padding: 0.0625rem 0.3125rem;
  -webkit-user-select: all;
          user-select: all;
}

.consvc-row__note {
  color: var(--muted-foreground);
  font-size: 0.75rem;
  line-height: 1.5;
  margin-top: 0.375rem;
  max-width: 74ch;
}

.consvc-row--stale .consvc-row__note {
  color: var(--warning);
}

.consvc-row--unusable .consvc-row__note {
  color: var(--destructive);
}

.consvc-row__actions {
  display: flex;
  flex: 0 0 auto;
  flex-wrap: wrap;
  gap: 0.375rem;
  justify-content: flex-end;
}

/* The card header is a single non-wrapping flex row, so its actions must never
   claim a full basis the way a row's action group does on a phone. */
.consvc-head-actions {
  align-items: center;
  display: flex;
  flex: 0 0 auto;
  gap: 0.5rem;
}

.consvc-empty {
  background: var(--page-bg-subtle);
  border: 1px dashed var(--border);
  border-radius: var(--radius-sm);
  color: var(--muted-foreground);
  font-size: 0.8125rem;
  line-height: 1.55;
  padding: 0.875rem;
}

.consvc-empty strong {
  color: var(--foreground);
}

/* ---------- the credential dialog ---------- */

.consvc-dialog {
  background: var(--overlay);
  display: block;
}

.consvc-dialog__help {
  color: var(--muted-foreground);
  font-size: 0.8125rem;
  line-height: 1.55;
  margin-bottom: 0.875rem;
}

.consvc-dialog__field {
  margin-bottom: 0.75rem;
}

.consvc-dialog__label {
  color: var(--muted-foreground);
  display: block;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  margin-bottom: 0.25rem;
  text-transform: uppercase;
}

/* The actor's own password is a different question from the service's
   credentials ("prove it is you" vs "what are they there"), so it is separated
   by a rule instead of being the fourth box in one undifferentiated stack. */
.consvc-dialog__actor {
  border-top: 1px solid var(--border);
  margin-top: 0.875rem;
  padding-top: 0.875rem;
}

.consvc-dialog__note {
  background: var(--info-soft);
  border-radius: var(--radius-sm);
  color: var(--info);
  font-size: 0.75rem;
  line-height: 1.5;
  margin-top: 0.5rem;
  padding: 0.5rem 0.625rem;
}

.consvc-dialog__note--plain {
  background: var(--page-bg-subtle);
  color: var(--muted-foreground);
}

.consvc-dialog__error {
  background: var(--destructive-soft);
  border-radius: var(--radius-sm);
  color: var(--destructive);
  font-size: 0.75rem;
  line-height: 1.5;
  margin-bottom: 0.75rem;
  padding: 0.5rem 0.625rem;
}

/* ---------- small screens ---------- */

@media (max-width: 575.98px) {
  .consvc-row {
    flex-wrap: wrap;
  }

  /* A 2-button action group beside a wrapped name column ends up one button per
     line at the right-hand edge; below the body it stays a single row. */
  .consvc-row__actions {
    flex-basis: 100%;
    justify-content: flex-start;
  }
}

/* ---------- the icon gap Bootstrap 5 no longer supplies ---------- */

/*
 * \`mr-1\` is Bootstrap 4. Bootstrap 5 (5.3.6 here) renamed it to \`me-1\` and
 * defines nothing called \`mr-1\`, so every \`<i className="fa fa-x mr-1">\` in
 * this repo — 47 of them before this screen — renders flush against the word
 * after it. The class is left in the markup as the house idiom and the spacing
 * is supplied here, scoped to this screen so it changes nothing else.
 */
.consvc-row__note > i,
.consvc-dialog__note > i,
.consvc-head-actions .btn > i,
.consvc-row__actions .btn > i {
  margin-right: 0.375rem;
}
`, "",{"version":3,"sources":["webpack://./src/pages/settings/styles/connected-services.css"],"names":[],"mappings":"AAAA;;;;;;;;;;;;;;EAcE;;AAEF;;;2DAG2D;AAC3D;EACE,sBAAsB;AACxB;;AAEA;EACE,yBAAyB;AAC3B;;AAEA;EACE,wBAAwB;EACxB,6BAA6B;EAC7B,gBAAgB;AAClB;;AAEA;EACE,8BAA8B;EAC9B,oBAAoB;EACpB,eAAe;AACjB;;AAEA,6DAA6D;;AAE7D;;wEAEwE;AACxE;EACE,aAAa;EACb,aAAa;EACb,sEAAsE;AACxE;;AAEA;EACE,2CAA2C;EAC3C,qBAAqB;AACvB;;AAEA;EACE,iCAAiC;AACnC;;AAEA;EACE,mBAAmB;EACnB,wBAAwB;EACxB,aAAa;EACb,oBAAoB;EACpB,gBAAgB;EAChB,aAAa;EACb,sBAAsB;AACxB;;AAEA;EACE,8BAA8B;EAC9B,kBAAkB;AACpB;;AAEA;EACE,qBAAqB;AACvB;;AAEA;EACE,8BAA8B;EAC9B,kBAAkB;EAClB,gBAAgB;EAChB,SAAS;AACX;;AAEA,mCAAmC;;AAEnC;EACE,8BAA8B;EAC9B,kBAAkB;EAClB,gBAAgB;EAChB,mBAAmB;EACnB,eAAe;AACjB;;AAEA;EACE,8BAA8B;EAC9B,6BAA6B;EAC7B,kBAAkB;EAClB,gBAAgB;AAClB;;AAEA,0CAA0C;;AAE1C;EACE,aAAa;EACb,sBAAsB;EACtB,WAAW;AACb;;AAEA;EACE,uBAAuB;EACvB,uBAAuB;EACvB,+BAA+B;EAC/B,4BAA4B;EAC5B,aAAa;EACb,YAAY;EACZ,gBAAgB;EAChB,kFAAkF;AACpF;;AAEA;EACE,6BAA6B;AAC/B;;AAEA;;;;;4EAK4E;AAC5E;EACE,+BAA+B;EAC/B,4BAA4B;AAC9B;;AAEA;EACE,+BAA+B;AACjC;;AAEA;EACE,mCAAmC;EACnC,gCAAgC;AAClC;;AAEA;EACE,mCAAmC;AACrC;;AAEA;8DAC8D;AAC9D;EACE,wBAAwB;EACxB,oBAAoB;AACtB;;AAEA;EACE,wBAAwB;AAC1B;;AAEA;EACE,mBAAmB;EACnB,gDAAgD;EAChD,+BAA+B;EAC/B,gCAAgC;EAChC,aAAa;EACb,cAAc;EACd,6BAA6B;EAC7B,oBAAoB;EACpB,gBAAgB;EAChB,eAAe;EACf,uBAAuB;EACvB,sBAAsB;EACtB,cAAc;AAChB;;AAEA;EACE,4BAA4B;EAC5B,kCAAkC;AACpC;;AAEA;EACE,cAAc;EACd,YAAY;AACd;;AAEA;EACE,mBAAmB;EACnB,wBAAwB;EACxB,aAAa;EACb,eAAe;EACf,mBAAmB;EACnB,gBAAgB;EAChB,oBAAoB;AACtB;;AAEA;EACE,8BAA8B;EAC9B,gBAAgB;AAClB;;AAEA;EACE,8BAA8B;EAC9B,kBAAkB;EAClB,gBAAgB;EAChB,qBAAqB;EACrB,uBAAuB;AACzB;;AAEA;iFACiF;AACjF;EACE,4BAA4B;EAC5B,+BAA+B;EAC/B,kCAAkC;EAClC,6BAA6B;EAC7B,oBAAoB;EACpB,4BAA4B;EAC5B,wBAAgB;UAAhB,gBAAgB;AAClB;;AAEA;EACE,8BAA8B;EAC9B,kBAAkB;EAClB,gBAAgB;EAChB,oBAAoB;EACpB,eAAe;AACjB;;AAEA;EACE,qBAAqB;AACvB;;AAEA;EACE,yBAAyB;AAC3B;;AAEA;EACE,aAAa;EACb,cAAc;EACd,eAAe;EACf,aAAa;EACb,yBAAyB;AAC3B;;AAEA;qEACqE;AACrE;EACE,mBAAmB;EACnB,aAAa;EACb,cAAc;EACd,WAAW;AACb;;AAEA;EACE,iCAAiC;EACjC,gCAAgC;EAChC,+BAA+B;EAC/B,8BAA8B;EAC9B,oBAAoB;EACpB,iBAAiB;EACjB,iBAAiB;AACnB;;AAEA;EACE,wBAAwB;AAC1B;;AAEA,gDAAgD;;AAEhD;EACE,0BAA0B;EAC1B,cAAc;AAChB;;AAEA;EACE,8BAA8B;EAC9B,oBAAoB;EACpB,iBAAiB;EACjB,uBAAuB;AACzB;;AAEA;EACE,sBAAsB;AACxB;;AAEA;EACE,8BAA8B;EAC9B,cAAc;EACd,kBAAkB;EAClB,gBAAgB;EAChB,sBAAsB;EACtB,sBAAsB;EACtB,yBAAyB;AAC3B;;AAEA;;6EAE6E;AAC7E;EACE,mCAAmC;EACnC,oBAAoB;EACpB,qBAAqB;AACvB;;AAEA;EACE,4BAA4B;EAC5B,+BAA+B;EAC/B,kBAAkB;EAClB,kBAAkB;EAClB,gBAAgB;EAChB,kBAAkB;EAClB,wBAAwB;AAC1B;;AAEA;EACE,iCAAiC;EACjC,8BAA8B;AAChC;;AAEA;EACE,mCAAmC;EACnC,+BAA+B;EAC/B,yBAAyB;EACzB,kBAAkB;EAClB,gBAAgB;EAChB,sBAAsB;EACtB,wBAAwB;AAC1B;;AAEA,wCAAwC;;AAExC;EACE;IACE,eAAe;EACjB;;EAEA;yEACuE;EACvE;IACE,gBAAgB;IAChB,2BAA2B;EAC7B;AACF;;AAEA,sEAAsE;;AAEtE;;;;;;EAME;AACF;;;;EAIE,sBAAsB;AACxB","sourcesContent":["/*\n * Connected services (Settings).\n *\n * Every colour is a design token (src/styles/theme.css) so both themes stay\n * correct — no hardcoded hex anywhere, per DESIGN_GUIDE.md. Buttons, badges,\n * cards, form inputs and the modal shell come from the atoms and the global\n * theme-overrides layer; this file only adds the layout that is specific to\n * this screen.\n *\n * The one inline style in the JSX is `--consvc-accent`, which is set to\n * `var(--chart-N)` from the provider descriptor's accentIndex. That is a custom\n * property pointing AT a token, not a colour literal: the accent has to be\n * chosen per provider at runtime, and a provider added on the server must get\n * its monogram colour with no CSS change here.\n */\n\n/* Used as a hook in the JSX and previously styled nowhere. The bottom padding\n   is load-bearing: the last card is `mt-2` with no bottom margin, so without it\n   the page ended flush against the viewport edge on every layout that does not\n   happen to render the optional \"Not available\" section. */\n.consvc-page {\n  padding-bottom: 1.5rem;\n}\n\n.consvc-page__head {\n  padding: 0.25rem 0 0.5rem;\n}\n\n.consvc-page__title {\n  color: var(--foreground);\n  font-family: var(--font-sans);\n  font-weight: 700;\n}\n\n.consvc-page__subtitle {\n  color: var(--muted-foreground);\n  font-size: 0.8125rem;\n  max-width: 72ch;\n}\n\n/* ---------- \"how a service connects\" explainer ---------- */\n\n/* Three short statements rather than a paragraph: the first one is the whole\n   mental model (signing in IS the connect), and a reader who skims a paragraph\n   misses it and then looks for a button that they mostly do not need. */\n.consvc-how {\n  display: grid;\n  gap: 0.875rem;\n  grid-template-columns: repeat(auto-fit, minmax(min(17rem, 100%), 1fr));\n}\n\n.consvc-how__item {\n  border-left: 2px solid var(--border-strong);\n  padding-left: 0.75rem;\n}\n\n.consvc-how__item--key {\n  border-left-color: var(--primary);\n}\n\n.consvc-how__title {\n  align-items: center;\n  color: var(--foreground);\n  display: flex;\n  font-size: 0.8125rem;\n  font-weight: 600;\n  gap: 0.375rem;\n  margin-bottom: 0.25rem;\n}\n\n.consvc-how__title i {\n  color: var(--muted-foreground);\n  font-size: 0.75rem;\n}\n\n.consvc-how__item--key .consvc-how__title i {\n  color: var(--primary);\n}\n\n.consvc-how__text {\n  color: var(--muted-foreground);\n  font-size: 0.75rem;\n  line-height: 1.5;\n  margin: 0;\n}\n\n/* ---------- sections ---------- */\n\n.consvc-section__hint {\n  color: var(--muted-foreground);\n  font-size: 0.75rem;\n  line-height: 1.5;\n  margin: 0 0 0.75rem;\n  max-width: 78ch;\n}\n\n.consvc-section__count {\n  color: var(--muted-foreground);\n  font-family: var(--font-mono);\n  font-size: 0.75rem;\n  font-weight: 500;\n}\n\n/* ---------- one service row ---------- */\n\n.consvc-list {\n  display: flex;\n  flex-direction: column;\n  gap: 0.5rem;\n}\n\n.consvc-row {\n  align-items: flex-start;\n  background: var(--card);\n  border: 1px solid var(--border);\n  border-radius: var(--radius);\n  display: flex;\n  gap: 0.75rem;\n  padding: 0.75rem;\n  transition: background var(--transition-fast), border-color var(--transition-fast);\n}\n\n.consvc-row:hover {\n  background: var(--card-hover);\n}\n\n/* Two states of a link, painted in order of severity. Stale is a warning: still\n   on file and still usable, just not confirmed recently. Unusable is past the\n   ceiling — it may NOT be used as an export target until somebody re-checks it,\n   so it is the louder of the two, not the quieter. (It was originally painted\n   inert, sharing a rule with \"not available on this deployment\", while carrying\n   a danger badge — the ground and the chip disagreed about the same row.) */\n.consvc-row--stale {\n  background: var(--warning-soft);\n  border-color: var(--warning);\n}\n\n.consvc-row--stale:hover {\n  background: var(--warning-soft);\n}\n\n.consvc-row--unusable {\n  background: var(--destructive-soft);\n  border-color: var(--destructive);\n}\n\n.consvc-row--unusable:hover {\n  background: var(--destructive-soft);\n}\n\n/* Not a state of a link — a statement about the deployment. It is inert, so it\n   is the one row that reads as absent rather than as wrong. */\n.consvc-row--unavailable {\n  background: var(--muted);\n  border-style: dashed;\n}\n\n.consvc-row--unavailable:hover {\n  background: var(--muted);\n}\n\n.consvc-row__mono {\n  align-items: center;\n  background: var(--consvc-accent, var(--primary));\n  border-radius: var(--radius-sm);\n  color: var(--primary-foreground);\n  display: flex;\n  flex: 0 0 auto;\n  font-family: var(--font-sans);\n  font-size: 0.8125rem;\n  font-weight: 700;\n  height: 2.25rem;\n  justify-content: center;\n  letter-spacing: 0.01em;\n  width: 2.25rem;\n}\n\n.consvc-row--unavailable .consvc-row__mono {\n  background: var(--secondary);\n  color: var(--secondary-foreground);\n}\n\n.consvc-row__body {\n  flex: 1 1 auto;\n  min-width: 0;\n}\n\n.consvc-row__name {\n  align-items: center;\n  color: var(--foreground);\n  display: flex;\n  flex-wrap: wrap;\n  font-size: 0.875rem;\n  font-weight: 600;\n  gap: 0.375rem 0.5rem;\n}\n\n.consvc-row__site {\n  color: var(--muted-foreground);\n  font-weight: 500;\n}\n\n.consvc-row__meta {\n  color: var(--muted-foreground);\n  font-size: 0.75rem;\n  line-height: 1.5;\n  margin-top: 0.1875rem;\n  overflow-wrap: anywhere;\n}\n\n/* The external id is the one thing an operator is ever asked to read back to\n   Gemmiz support, so it is selectable as a whole rather than a word at a time. */\n.consvc-row__id {\n  background: var(--secondary);\n  border-radius: var(--radius-sm);\n  color: var(--secondary-foreground);\n  font-family: var(--font-mono);\n  font-size: 0.6875rem;\n  padding: 0.0625rem 0.3125rem;\n  user-select: all;\n}\n\n.consvc-row__note {\n  color: var(--muted-foreground);\n  font-size: 0.75rem;\n  line-height: 1.5;\n  margin-top: 0.375rem;\n  max-width: 74ch;\n}\n\n.consvc-row--stale .consvc-row__note {\n  color: var(--warning);\n}\n\n.consvc-row--unusable .consvc-row__note {\n  color: var(--destructive);\n}\n\n.consvc-row__actions {\n  display: flex;\n  flex: 0 0 auto;\n  flex-wrap: wrap;\n  gap: 0.375rem;\n  justify-content: flex-end;\n}\n\n/* The card header is a single non-wrapping flex row, so its actions must never\n   claim a full basis the way a row's action group does on a phone. */\n.consvc-head-actions {\n  align-items: center;\n  display: flex;\n  flex: 0 0 auto;\n  gap: 0.5rem;\n}\n\n.consvc-empty {\n  background: var(--page-bg-subtle);\n  border: 1px dashed var(--border);\n  border-radius: var(--radius-sm);\n  color: var(--muted-foreground);\n  font-size: 0.8125rem;\n  line-height: 1.55;\n  padding: 0.875rem;\n}\n\n.consvc-empty strong {\n  color: var(--foreground);\n}\n\n/* ---------- the credential dialog ---------- */\n\n.consvc-dialog {\n  background: var(--overlay);\n  display: block;\n}\n\n.consvc-dialog__help {\n  color: var(--muted-foreground);\n  font-size: 0.8125rem;\n  line-height: 1.55;\n  margin-bottom: 0.875rem;\n}\n\n.consvc-dialog__field {\n  margin-bottom: 0.75rem;\n}\n\n.consvc-dialog__label {\n  color: var(--muted-foreground);\n  display: block;\n  font-size: 0.75rem;\n  font-weight: 600;\n  letter-spacing: 0.01em;\n  margin-bottom: 0.25rem;\n  text-transform: uppercase;\n}\n\n/* The actor's own password is a different question from the service's\n   credentials (\"prove it is you\" vs \"what are they there\"), so it is separated\n   by a rule instead of being the fourth box in one undifferentiated stack. */\n.consvc-dialog__actor {\n  border-top: 1px solid var(--border);\n  margin-top: 0.875rem;\n  padding-top: 0.875rem;\n}\n\n.consvc-dialog__note {\n  background: var(--info-soft);\n  border-radius: var(--radius-sm);\n  color: var(--info);\n  font-size: 0.75rem;\n  line-height: 1.5;\n  margin-top: 0.5rem;\n  padding: 0.5rem 0.625rem;\n}\n\n.consvc-dialog__note--plain {\n  background: var(--page-bg-subtle);\n  color: var(--muted-foreground);\n}\n\n.consvc-dialog__error {\n  background: var(--destructive-soft);\n  border-radius: var(--radius-sm);\n  color: var(--destructive);\n  font-size: 0.75rem;\n  line-height: 1.5;\n  margin-bottom: 0.75rem;\n  padding: 0.5rem 0.625rem;\n}\n\n/* ---------- small screens ---------- */\n\n@media (max-width: 575.98px) {\n  .consvc-row {\n    flex-wrap: wrap;\n  }\n\n  /* A 2-button action group beside a wrapped name column ends up one button per\n     line at the right-hand edge; below the body it stays a single row. */\n  .consvc-row__actions {\n    flex-basis: 100%;\n    justify-content: flex-start;\n  }\n}\n\n/* ---------- the icon gap Bootstrap 5 no longer supplies ---------- */\n\n/*\n * `mr-1` is Bootstrap 4. Bootstrap 5 (5.3.6 here) renamed it to `me-1` and\n * defines nothing called `mr-1`, so every `<i className=\"fa fa-x mr-1\">` in\n * this repo — 47 of them before this screen — renders flush against the word\n * after it. The class is left in the markup as the house idiom and the spacing\n * is supplied here, scoped to this screen so it changes nothing else.\n */\n.consvc-row__note > i,\n.consvc-dialog__note > i,\n.consvc-head-actions .btn > i,\n.consvc-row__actions .btn > i {\n  margin-right: 0.375rem;\n}\n"],"sourceRoot":""}]);
// Exports
___CSS_LOADER_EXPORT___.locals = {};
export default ___CSS_LOADER_EXPORT___;
